import { useState, useEffect } from 'react'
import { useCanAccess } from '../../hooks/useCanAccess'
import { useSelectionStore } from '../../stores/selectionStore'
import { useBuildingStore } from '../../stores/buildingStore'
import { wallApi } from '../../services/wallApi'
import type { Wall } from '../../types'
import '../../styles/editors.css'

const WALL_COLORS = [
  '#8b9dc3', '#e94560', '#4ade80', '#60a5fa',
  '#fb923c', '#a78bfa', '#f472b6', '#fbbf24',
  '#2dd4bf', '#94a3b8', '#f87171', '#34d399',
  '#818cf8', '#c084fc', '#22d3ee', '#ffffff',
]

const WALL_TEXTURES = [
  { value: 'solid', label: 'Plein' },
  { value: 'glass', label: 'Verre' },
  { value: 'fabric', label: 'Tissu' },
] as const

export default function WallEditor() {
  const selectedWall = useSelectionStore((s) => s.selectedWall)
  const selectWall = useSelectionStore((s) => s.selectWall)
  const editMode = useSelectionStore((s) => s.editMode)
  const setEditMode = useSelectionStore((s) => s.setEditMode)
  const currentSiteId = useBuildingStore((s) => s.currentSiteId)
  const floors = useBuildingStore((s) => s.floors)
  const loadSite = useBuildingStore((s) => s.loadSite)

  const [form, setForm] = useState<Partial<Wall>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (selectedWall) {
      setForm({ ...selectedWall })
    } else {
      setForm({})
    }
  }, [selectedWall])

  const updateField = <K extends keyof Wall>(key: K, value: Wall[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    if (!currentSiteId || !selectedWall) return
    setSaving(true)
    setError(null)
    try {
      await wallApi.updateWall(currentSiteId, selectedWall.id, form)
      await loadSite(currentSiteId)
      selectWall(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur sauvegarde')
    }
    setSaving(false)
  }

  const handleDelete = async () => {
    if (!currentSiteId || !selectedWall) return
    setSaving(true)
    setError(null)
    try {
      await wallApi.deleteWall(currentSiteId, selectedWall.id)
      selectWall(null)
      await loadSite(currentSiteId)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur suppression')
    }
    setSaving(false)
  }

  const handleDuplicate = async () => {
    if (!currentSiteId || !selectedWall) return
    setSaving(true)
    setError(null)
    try {
      const newWall = await wallApi.createWall(currentSiteId, {
        x: selectedWall.x + 1,
        z: selectedWall.z,
        width: selectedWall.width,
        depth: selectedWall.depth,
        height: selectedWall.height,
        rotation: selectedWall.rotation,
        color: selectedWall.color,
        floor: selectedWall.floor,
        porteur: selectedWall.porteur,
      })
      await loadSite(currentSiteId)
      selectWall(newWall)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur duplication')
    }
    setSaving(false)
  }

  const handleCreate = async () => {
    if (!currentSiteId) return
    setSaving(true)
    setError(null)
    try {
      const newWall = await wallApi.createWall(currentSiteId, {
        x: 0,
        z: 0,
        width: 2,
        depth: 0.2,
        height: 3,
        rotation: 0,
        color: '#cccccc',
        floor: 0,
        porteur: false,
      })
      await loadSite(currentSiteId)
      selectWall(newWall)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur creation')
    }
    setSaving(false)
  }

  const canCreate = useCanAccess('walls', 'create')
  const canUpdate = useCanAccess('walls', 'update')
  const canDelete = useCanAccess('walls', 'delete')
  const isWallMode = editMode === 'wall'

  if (!canCreate && !canUpdate) return null

  return (
      <div className="editor-panel">
        <div className="panel-section__title">Editeur de murs</div>

        <div className="editor-panel__toolbar">
          <button
            className={`editor-panel__mode-btn ${isWallMode ? 'editor-panel__mode-btn--active' : ''}`}
            onClick={() => setEditMode(isWallMode ? 'none' : 'wall')}
          >
            {isWallMode ? 'Quitter edition' : 'Mode edition'}
          </button>
          {isWallMode && canCreate && (
            <button className="editor-panel__create-btn" onClick={handleCreate} disabled={saving}>
              + Nouveau mur
            </button>
          )}
        </div>

        {selectedWall && (
          <div className="editor-panel__form">
            <div className="editor-panel__row">
              <div className="editor-panel__field editor-panel__field--half">
                <label>X</label>
                <input type="number" step="0.1" value={form.x ?? 0} onChange={(e) => updateField('x', parseFloat(e.target.value))} />
              </div>
              <div className="editor-panel__field editor-panel__field--half">
                <label>Z</label>
                <input type="number" step="0.1" value={form.z ?? 0} onChange={(e) => updateField('z', parseFloat(e.target.value))} />
              </div>
            </div>

            <div className="editor-panel__row">
              <div className="editor-panel__field editor-panel__field--half">
                <label>Largeur</label>
                <input type="number" step="0.1" min="0.1" value={form.width ?? 2} onChange={(e) => updateField('width', parseFloat(e.target.value))} />
              </div>
              <div className="editor-panel__field editor-panel__field--half">
                <label>Profondeur</label>
                <input type="number" step="0.1" min="0.1" value={form.depth ?? 0.2} onChange={(e) => updateField('depth', parseFloat(e.target.value))} />
              </div>
            </div>

            <div className="editor-panel__field">
              <label>Hauteur</label>
              <input type="number" step="0.1" min="0.1" value={form.height ?? 3} onChange={(e) => updateField('height', parseFloat(e.target.value))} />
            </div>

            <div className="editor-panel__field">
              <label>Rotation: {form.rotation}°</label>
              <input type="range" min="0" max="360" step="1" value={form.rotation ?? 0} onChange={(e) => updateField('rotation', parseInt(e.target.value))} />
            </div>

            <div className="editor-panel__field">
              <label>Couleur</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 2 }}>
                {WALL_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => updateField('color', c)}
                    style={{
                      width: 24, height: 24, border: form.color === c ? '2px solid #fff' : '2px solid transparent',
                      borderRadius: 4, background: c, cursor: 'pointer', padding: 0,
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="editor-panel__field">
              <label>Texture</label>
              <select
                value={(form as any).texture ?? 'solid'}
                onChange={(e) => updateField('texture' as any, e.target.value)}
              >
                {WALL_TEXTURES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="editor-panel__field">
              <label>Etage</label>
              <select value={form.floor ?? 0} onChange={(e) => updateField('floor', parseInt(e.target.value))}>
                {floors.map((f) => (
                  <option key={f.index} value={f.index}>{f.name}</option>
                ))}
              </select>
            </div>

            <div className="editor-panel__field editor-panel__field--checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={form.porteur ?? false}
                  onChange={(e) => updateField('porteur', e.target.checked)}
                />
                Mur porteur
              </label>
            </div>

            {error && <div className="editor-panel__error">{error}</div>}

            <div className="editor-panel__actions">
              {canUpdate && (
                <button className="editor-panel__btn editor-panel__btn--save" onClick={handleSave} disabled={saving}>
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              )}
              {canCreate && (
                <button
                  className="editor-panel__btn editor-panel__btn--duplicate"
                  onClick={handleDuplicate}
                  disabled={saving}
                >
                  Dupliquer
                </button>
              )}
              {canDelete && (
                <button className="editor-panel__btn editor-panel__btn--delete" onClick={handleDelete} disabled={saving}>
                  Supprimer
                </button>
              )}
              <button className="editor-panel__btn editor-panel__btn--cancel" onClick={() => selectWall(null)}>
                Annuler
              </button>
            </div>
          </div>
        )}

        {isWallMode && !selectedWall && (
          <p className="editor-panel__hint">Cliquez sur un mur pour le modifier, ou creez-en un nouveau.</p>
        )}
      </div>
  )
}
